import * as React from 'react';
import { DataGrid } from './DataGrid';
import { measureScrollbarWidth } from './Utils';

/**
 * Component for handling default/native scrolling for the DataGrid.
 */
export class DefaultScrollComponent
  extends React.PureComponent<DataGrid.ScrollComponentProps, {}>
  implements DataGrid.IScrollable {

  private measuredScrollbarWidth: number = 17; // 17 is the default on basically all platforms
  private element: HTMLElement | null = null;

  private onScroll = (e: React.UIEvent<HTMLDivElement>): void => {
    this.props.onScroll(e.currentTarget.scrollLeft, e.currentTarget.scrollTop);
  }

  private handleRef = (element: HTMLDivElement | null): void => {
    this.element = element;
    this.props.onRefUpdated(element ? this : null);
    if (element) {
      this.measuredScrollbarWidth = measureScrollbarWidth(element);
    }
  }

  public render(): JSX.Element {
    return (
      <div className={this.props.rootCssClass}
            // tslint:disable-next-line:deprecation
            id={this.props.scrollingRootId}
            onScroll={this.onScroll}
            ref={this.handleRef}
            style={{
              top: this.props.marginTop,
              overflow: 'auto',
              WebkitOverflowScrolling: 'touch', // Defect 662023, enable inertial scrolling for mobile devices
              position: 'absolute',
              right: 0,
              bottom: 0,
              left: 0,
            }}
      >
        { this.props.children }
      </div>
    );
  }

  public getScrollbarWidth(): number {
    return this.measuredScrollbarWidth;
  }

  public getScrollPosition(): { scrollLeft: number, scrollTop: number } {
    if (!this.element) {
      return { scrollLeft: 0, scrollTop: 0 };
    }
    return { scrollLeft: this.element.scrollLeft, scrollTop: this.element.scrollTop };
  }

  public setScrollPosition(scrollLeft: number | null, scrollTop: number | null): void {
    if (!this.element) { return; }
    if (!!scrollLeft || scrollLeft === 0) {
      this.element.scrollLeft = scrollLeft;
    }
    if (!!scrollTop || scrollTop === 0) {
      this.element.scrollTop = scrollTop;
    }
  }

  public scrollBy(x: number, y: number): void {
    if (!this.element) { return; }
    const dom = this.element;
    const newLeft = Math.min(Math.max(dom.scrollLeft + x, 0), dom.scrollWidth);
    const newTop = Math.min(Math.max(dom.scrollTop + y, 0), dom.scrollHeight);

    this.setScrollPosition(newLeft, newTop);
  }

  /**
   * Native scroll doesn't need to clean up anything special
   */
  // tslint:disable-next-line:no-empty
  public dispose(): void {}

  public resetPosition(): void {
    this.setScrollPosition(0, 0);
  }

  /**
   * Native scroll doesn't need to do any special refreshing.
   */
  // tslint:disable-next-line:no-empty
  public refresh(): void {}

  /**
   * All events are handled natively, so nothing special to do here.
   * This also means however, that any interception of events might break scrolling.
   */
  // tslint:disable-next-line:no-empty
  public handleEvent(e: TouchEvent): void {}

  public canScroll(): boolean {
    if (!this.element) { return false; }
    const dom = this.element;
    const hasHorizontalScrollbar = dom.scrollWidth > dom.clientWidth;
    const hasVerticalScrollbar = dom.scrollHeight > dom.clientHeight;
    return hasHorizontalScrollbar || hasVerticalScrollbar;
  }

  public getScrollContainer(): Element | null {
    return this.element;
  }
}
